import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { getConfigPhotosForSelectedOptions } from 'data-hooks/usePDPPhotos/utils';
import { getOptionAdditionalPrice } from 'data-hooks/useProductPrice/utils';
import usePDPPhotos from 'data-hooks/usePDPPhotos';
import { PRODUCT_OPTION_URL_SUFFIX } from 'commons/constants';
import PDPAvailableInSizesRowUI from './PDPAvailableInSizesRowUI';
import usePDPOptions from '../../state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from '../../state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';

// Note: while this component is within PDP, it is used to render details
// about other products, so anything product-specific comes in through the
// detail prop. Only the selected options are read from PDP context.
const PDPAvailableInSizesRow = ({
  isActive,
  pdpProductId,
  detail,
  productsData = []
}) => {
  const {
    data: photosData
  } = usePDPPhotos();
  const {
    loading: optionsLoading,
    data: optionsData
  } = usePDPOptions();
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  const {
    upholsteryOptionId,
    selectedOptionsExtended
  } = optionsData;
  const basicDetails = detail?.basicDetails;
  const sizeVariantOptions = detail?.options;
  const sizeVariantStock = detail?.stock;
  const displayPhoto = useMemo(() => {
    if (optionsLoading) {
      return photosData?.configurationPhotos?.[0]?.src;
    }
    const configPhotos = getConfigPhotosForSelectedOptions(selectedOptionsVariantSku, selectedOptionsExtended, upholsteryOptionId, basicDetails);
    return configPhotos?.[0]?.src || photosData?.configurationPhotos?.[0]?.src || '';
  }, [basicDetails, optionsLoading, photosData, selectedOptionsExtended, selectedOptionsVariantSku, upholsteryOptionId]);
  const rowSelectedOptions = useMemo(() => {
    if (!selectedOptionsExtended || !sizeVariantOptions) return {};
    const mapping = {};
    Object.entries(selectedOptionsExtended).forEach(([optionId, optionData]) => {
      const selectedValueSku = optionData?.value?.sku;
      if (!selectedValueSku) return;
      const rowOption = sizeVariantOptions.find(opt => +opt.id === +optionId);
      if (!rowOption?.values) return;
      const matchingValue = rowOption.values.find(val => val.sku === selectedValueSku);
      if (matchingValue) {
        mapping[optionId] = matchingValue.id;
      }
    });
    return mapping;
  }, [selectedOptionsExtended, sizeVariantOptions]);
  const rowOptionPrice = useMemo(() => {
    if (!sizeVariantOptions) return 0;
    return getOptionAdditionalPrice(sizeVariantOptions, sizeVariantStock?.stockOptions, rowSelectedOptions, true, upholsteryOptionId);
  }, [sizeVariantOptions, sizeVariantStock, rowSelectedOptions, upholsteryOptionId]);
  const priceWithFabricUpcharge = useMemo(() => {
    if (!basicDetails?.meta?.price) return 0;
    const originalPrice = basicDetails.meta.price.original_price || 0;
    const discountPercent = basicDetails.meta.price.discount_percent || 0;
    const totalBeforeDiscount = originalPrice + rowOptionPrice;
    if (discountPercent > 0) {
      return Math.round(totalBeforeDiscount * (100 - discountPercent) / 100);
    }
    return totalBeforeDiscount;
  }, [basicDetails?.meta?.price, rowOptionPrice]);
  const urlKey = useMemo(() => {
    if (!basicDetails || !pdpProductId) {
      return '';
    }
    if (!upholsteryOptionId || isEmpty(selectedOptionsExtended) || !selectedOptionsExtended?.[upholsteryOptionId]?.value?.value) {
      return basicDetails.attributes_min?.urlKey || '';
    }
    const optionValue = selectedOptionsExtended[upholsteryOptionId].value.value;
    const formattedOptionValue = optionValue.toLowerCase().split(' ').join('_');
    return `${basicDetails?.attributes_min?.urlKey || ''}?${PRODUCT_OPTION_URL_SUFFIX[upholsteryOptionId]}=${formattedOptionValue}`;
  }, [selectedOptionsExtended, upholsteryOptionId, basicDetails, pdpProductId]);
  return <PDPAvailableInSizesRowUI dimensions={basicDetails?.attributes_min?.dimensions} id={basicDetails?.id} image={displayPhoto} isActive={isActive} loading={!detail} name={basicDetails?.name} price={priceWithFabricUpcharge} productsData={productsData} urlKey={urlKey} />;
};
PDPAvailableInSizesRow.propTypes = {
  isActive: PropTypes.bool,
  pdpProductId: PropTypes.number,
  detail: PropTypes.shape({
    basicDetails: PropTypes.object,
    options: PropTypes.arrayOf(PropTypes.object),
    stock: PropTypes.object,
    availability: PropTypes.object
  }),
  productsData: PropTypes.arrayOf(PropTypes.object)
};
export default PDPAvailableInSizesRow;